import type { AiReadingHistoryItem } from '@entities/saju'
import { useReadingHistoryQuery } from '@entities/saju'
import { usePollingStore } from '@shared/store/use-polling-store'
import { MenuRow } from '@shared/ui/menu-row'
import { Link } from '@tanstack/react-router'
import { useMemo } from 'react'
import { HiMiniSparkles } from 'react-icons/hi2'

export function ServerReadingHistory() {
  const { data: readingHistory } = useReadingHistoryQuery('classic')

  if (!readingHistory || readingHistory.length === 0) {
    return null
  }

  return <ReadingHistoryList readingHistory={readingHistory} />
}

export function LocalReadingHistory() {
  const pollingEntries = usePollingStore((s) => s.entries)

  const localHistory = useMemo<AiReadingHistoryItem[]>(() => {
    return Object.entries(pollingEntries).map(([hashKey, entry]) => ({
      hashKey,
      status:
        entry.status === 'completed' || entry.status === 'viewed' ? 'COMPLETED' : 'PROCESSING',
      name: entry.sajuName,
      createdAt: new Date(entry.startedAt).toISOString(),
    }))
  }, [pollingEntries])

  if (localHistory.length === 0) {
    return null
  }

  return <ReadingHistoryList readingHistory={localHistory} isLocal />
}

function ReadingHistoryList({
  readingHistory,
  isLocal,
}: {
  readonly readingHistory: AiReadingHistoryItem[]
  readonly isLocal?: boolean
}) {
  return (
    <MenuRow.Accordion
      icon={HiMiniSparkles}
      label={isLocal ? '이 브라우저에 저장된 분석 내역' : '분석 내역'}
      count={readingHistory.length}
      defaultOpen
    >
      <div className="flex flex-col gap-2 pb-3 pt-1">
        {readingHistory.map((item, historyIndex) => (
          <>
            <Link
              key={item.hashKey}
              to="/saju/$sajuType/result"
              params={{ sajuType: 'classic' }}
              search={{ hashKey: item.hashKey }}
              className="flex w-full px-4 items-center justify-between rounded-lg py-3 text-left transition-colors hover:bg-muted/30"
            >
              <div className="flex flex-col gap-0.5">
                <span className="typo-label font-medium text-foreground">{item.name}</span>
                <span className="typo-footnote text-muted-foreground">
                  {formatDate(item.createdAt)}
                </span>
              </div>
              <span className="typo-body font-bold text-foreground">
                {item.status === 'PROCESSING' ? '진행중 ' : '완료'}
              </span>
            </Link>
            {historyIndex !== readingHistory.length - 1 && (
              <hr className="divider divider-border" />
            )}
          </>
        ))}
      </div>
    </MenuRow.Accordion>
  )
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr)
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`
}
